import React from "react";

// ---------- helpers ----------
const parseDate = (s) => new Date(s + "T00:00:00Z");
const dayDiff = (a, b) => Math.round((parseDate(b) - parseDate(a)) / 86400000);
const addDays = (s, n) => {
  const d = parseDate(s); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0,10);
};
const monthIn = (date, allowed) => {
  if (!allowed || allowed.length === 0) return true;
  const m = parseDate(date).getUTCMonth()+1;
  return allowed.includes(m);
};
const waterYear = (date, startMonth=1) => {
  const d = parseDate(date);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth()+1;
  return m >= startMonth ? y : (y - 1);
};

// Expect daily series with no duplicate dates. Gaps are allowed (they break "consecutive").
function normalizeSeries(series) {
  // Ensure sorted by date asc
  return [...series].sort((a,b)=> (a.date < b.date ? -1 : 1));
}

// ---------- event detectors ----------
/**
 * Find "threshold-duration" events:
 * flow >= threshold for >= minDays consecutive days, restricted to allowed months.
 * Returns list of {startDate, endDate, days, type:'threshold'} (non-overlapping; uses cooldown=minDays).
 */
function findThresholdEvents(series, { threshold, minDays, allowedMonths, cooldownDays=null }) {
  const data = normalizeSeries(series);
  const N = data.length;
  const events = [];
  let i = 0;
  const cool = cooldownDays ?? minDays;

  while (i < N) {
    // Build a run of consecutive calendar days meeting threshold and month constraint
    let runLen = 0;
    let runStart = null, lastDate = null;

    let j = i;
    while (j < N) {
      const okMonth = monthIn(data[j].date, allowedMonths);
      const okFlow  = Number(data[j].flow) >= threshold;
      const isConsecutive = (runLen === 0) || (dayDiff(lastDate, data[j].date) === 1);

      if (okMonth && okFlow && (runLen === 0 || isConsecutive)) {
        if (runLen === 0) runStart = data[j].date;
        runLen += 1;
        lastDate = data[j].date;
        j++;
      } else {
        break;
      }
    }

    if (runLen >= minDays) {
      const startDate = runStart;
      const endDate   = lastDate;
      events.push({ startDate, endDate, days: runLen, type: 'threshold' });
      // cooldown to avoid counting overlapping events
      const targetResume = addDays(endDate, cool);
      while (i < N && data[i].date < targetResume) i++;
    } else {
      i = (runLen > 0) ? i + 1 : i + 1;
    }
  }
  return events;
}

/**
 * Find "volume-duration" events:
 * any sliding window of windowDays with sum(flow) >= volume, allowedMonths restriction.
 * Returns {startDate, endDate, days:windowDays, volume, type:'volume'} windows; uses cooldown=windowDays.
 */
function findVolumeEvents(series, { volume, windowDays, allowedMonths, cooldownDays=null }) {
  const data = normalizeSeries(series);
  const N = data.length;
  const events = [];
  const cool = cooldownDays ?? windowDays;

  // Two-pointer sliding window; requires exact consecutive days
  let i = 0;
  while (i < N) {
    // Start only on an allowed-month day
    if (!monthIn(data[i].date, allowedMonths)) { i++; continue; }

    // Build a consecutive block of length windowDays
    let j = i, sum = 0, ok = true;
    for (let d = 0; d < windowDays; d++) {
      const k = i + d;
      if (k >= N) { ok = false; break; }
      if (d > 0 && dayDiff(data[i + d - 1].date, data[k].date) !== 1) { ok = false; break; }
      if (!monthIn(data[k].date, allowedMonths)) { ok = false; break; }
      sum += Number(data[k].flow) || 0;
      j = k;
    }
    if (ok && sum >= volume) {
      events.push({ startDate: data[i].date, endDate: data[j].date, days: windowDays, volume: sum, type: 'volume' });
      // cooldown
      const targetResume = addDays(data[j].date, cool);
      // skip ahead to date >= targetResume
      while (i < N && data[i].date < targetResume) i++;
    } else {
      i++;
    }
  }
  return events;
}

// ---------- scoring ----------
/**
 * Compute frequency metrics and pass/fail vs targets.
 * @param {Array<{date,flow}>} series daily flows
 * @param {Object} measure configuration (see below)
 * @param {Object} [opts] { yearStartMonth: number } // e.g., 8 for Aug–Jul water years
 */
function evaluateMeasure(series, measure, opts={}) {
  const yearStartMonth = measure.yearStartMonth ?? opts.yearStartMonth ?? 1; // 1=Jan
  const allowedMonths = measure.allowedMonths ?? null;

  let events = [];
  if (measure.kind === 'threshold') {
    events = findThresholdEvents(series, {
      threshold: measure.thresholdMLd,
      minDays: measure.minDays,
      allowedMonths,
      cooldownDays: measure.cooldownDays
    });
  } else if (measure.kind === 'volume') {
    events = findVolumeEvents(series, {
      volume: measure.volumeML,
      windowDays: measure.windowDays,
      allowedMonths,
      cooldownDays: measure.cooldownDays
    });
  } else {
    throw new Error(`Unknown measure.kind: ${measure.kind}`);
  }

  // Group by water year for "% years with >=1 event"
  const years = new Map();
  for (const d of normalizeSeries(series)) {
    const wy = waterYear(d.date, yearStartMonth);
    years.set(wy, true);
  }
  const totalYears = years.size;

  const eventsByYear = new Map();
  for (const e of events) {
    const wy = waterYear(e.startDate, yearStartMonth);
    eventsByYear.set(wy, (eventsByYear.get(wy) || 0) + 1);
  }
  const yearsWithEvent = Array.from(eventsByYear.keys()).length;
  const pctYearsWithEvent = totalYears > 0 ? (yearsWithEvent / totalYears) : 0;

  // Inter-event gaps (days) and average return interval in years
  const byStart = [...events].sort((a,b)=> (a.startDate < b.startDate ? -1 : 1));
  const gapsDays = [];
  for (let i=1;i<byStart.length;i++){
    gapsDays.push(dayDiff(byStart[i-1].endDate, byStart[i].startDate));
  }
  const avgGapDays = gapsDays.length ? (gapsDays.reduce((a,b)=>a+b,0)/gapsDays.length) : null;
  const avgReturnYears = avgGapDays != null ? (avgGapDays / 365.25) : null;
  const maxGapDays = gapsDays.length ? Math.max(...gapsDays) : null;

  // Score against possible target types
  const t = measure.target || {};
  const pass = {};
  if (t.minPctYearsWithEvent != null)
    pass.minPctYearsWithEvent = (pctYearsWithEvent >= t.minPctYearsWithEvent);

  if (t.maxGapDays != null && maxGapDays != null)
    pass.maxGapDays = (maxGapDays <= t.maxGapDays);

  if (t.avgReturnYears != null && avgReturnYears != null)
    pass.avgReturnYears = (avgReturnYears <= t.avgReturnYears);

  if (t.eventsPerNYears && totalYears > 0) {
    const need = t.eventsPerNYears.events;
    const window = t.eventsPerNYears.years;
    // rolling window count
    let meets = false;
    if (byStart.length) {
      for (let s = 0; s < byStart.length; s++) {
        const start = parseDate(byStart[s].startDate);
        const end = new Date(start); end.setUTCFullYear(end.getUTCFullYear()+window);
        const count = byStart.filter(ev => parseDate(ev.startDate) >= start && parseDate(ev.startDate) < end).length;
        if (count >= need) { meets = true; break; }
      }
    }
    pass.eventsPerNYears = meets;
  }

  return {
    id: measure.id,
    label: measure.label,
    kind: measure.kind,
    observed: {
      events,
      totalYears,
      yearsWithEvent,
      pctYearsWithEvent,
      avgReturnYears,
      maxGapDays
    },
    target: measure.target,
    pass
  };
}

/**
 * Evaluate multiple measures.
 */
function evaluateAllMeasures(series, measures, opts={}) {
  return measures.map(m => evaluateMeasure(series, m, opts));
}

const MEASURES = [
  // Drought refugia — any flow; max gap between events
  // Weilmoringle (Culgoa) — max 350 d (low); 430 d (high) between events
  {
    id: "culgoa-weilmoringle-refugia-low",
    label: "Drought refugia (Weilmoringle, Culgoa) — low",
    kind: "threshold",
    thresholdMLd: 0,          // "Any flow"
    minDays: 1,
    allowedMonths: null,      // any time of year
    cooldownDays: 1,
    yearStartMonth: 1,        // calendar years are fine here
    target: { maxGapDays: 350 }
  },
  {
    id: "culgoa-weilmoringle-refugia-high",
    label: "Drought refugia (Weilmoringle, Culgoa) — high",
    kind: "threshold",
    thresholdMLd: 0, minDays: 1, allowedMonths: null, cooldownDays: 1,
    target: { maxGapDays: 430 }
  },

  // Longitudinal connectivity — % years with >=1 event
  { // Brenda (Culgoa) 1,000 ML/d for 7 d, any time; 90% (low) / 80% (high)
    id: "culgoa-brenda-conn-1000x7-low",
    label: "Longitudinal connectivity (Brenda — 1,000 ML/d for 7 d)",
    kind: "threshold",
    thresholdMLd: 1000, minDays: 7, allowedMonths: null, cooldownDays: 7,
    target: { minPctYearsWithEvent: 0.90 }
  },
  {
    id: "culgoa-brenda-conn-3500x14-low",
    label: "Longitudinal connectivity (Brenda — 3,500 ML/d for 14 d, Aug–May)",
    kind: "threshold",
    thresholdMLd: 3500, minDays: 14,
    allowedMonths: [8,9,10,11,12,1,2,3,4,5], // Aug–May
    cooldownDays: 14,
    target: { minPctYearsWithEvent: 0.60 }
  },

  // Lateral connectivity — average return interval
  { // Riparian zone: 9,200 ML/d for 12 d, any time; 2 yrs (low) / 3 yrs (high)
    id: "culgoa-brenda-lateral-riparian-low",
    label: "Lateral connectivity – riparian (Brenda — 9,200 ML/d for 12 d)",
    kind: "threshold",
    thresholdMLd: 9200, minDays: 12, allowedMonths: null, cooldownDays: 12,
    target: { avgReturnYears: 2.0 }
  },

  // Narran Lakes — volume-duration (bird breeding etc.)
  { // 154,000 ML over 90 d; “2 events in any 8–10 years”
    id: "narran-154k-90d",
    label: "Narran Lakes waterbird breeding (Wilby Wilby — 154,000 ML in 90 d)",
    kind: "volume",
    volumeML: 154000, windowDays: 90,
    allowedMonths: null, cooldownDays: 90,
    // Implement the stricter variant (≥2 events in any 10 years)
    target: { eventsPerNYears: { events: 2, years: 10 } }
  },
  { // 50,000 ML over 90 d; avg 1.3–1.7 yr (use ≤1.7 yr as a pass)
    id: "narran-50k-90d",
    label: "Narran Lakes – larger area of waterbird habitat (50,000 ML in 90 d)",
    kind: "volume",
    volumeML: 50000, windowDays: 90,
    allowedMonths: null, cooldownDays: 90,
    target: { avgReturnYears: 1.7 }
  }
];



export default function CondamineBalonne(props) {

  const { master=[]} = props;

  console.log({props});

  const results = React.useMemo(() => {
    MEASURES.forEach(measure => {
      console.log(measure);
    })
  }, [master]);


  return <div style={{marginTop: 48}}>
    <h1>Custom PM Info Sheet For Condamine - Balonne</h1>
  </div>
}